import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { PLAN_OPTIONS, ROLE_OPTIONS } from "../../lib/b2b";

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="mb-1 block text-sm font-semibold text-[#0B1F3A]">{label}{required && <span className="text-[#F97316]"> *</span>}</span>
    {children}
  </label>
);
const inputCls = "w-full rounded-xl border border-[#0B1F3A]/15 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#0E7C7B]";

export function LeadForm({ defaultPlan = "", selectorAnswers = null }) {
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "", company: "", role: "", email: "", phone: "", units: "", property_type: "",
    zones: "", size_range: "", splits_per_unit: "", plan_interest: defaultPlan,
    current_management: "", main_problems: "", preferred_time: "", message: "",
    rooms: "", common_areas: "", company_website: "",
  });
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const isHotel = f.role === "Struttura ricettiva";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!privacy) { setError("È necessario accettare l'informativa privacy."); return; }
    setBusy(true);
    try {
      const payload = { ...f, privacy_consent: privacy, marketing_consent: marketing, selector_answers: selectorAnswers };
      const { data } = await api.post("/b2b/leads", payload);
      setDone(data.code);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.response?.data?.detail || "Si è verificato un errore. Riprova.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="lead-success" className="rounded-3xl border border-[#0E7C7B]/30 bg-white p-8 text-center shadow-xl">
        <CheckCircle2 className="mx-auto h-14 w-14 text-[#0E7C7B]" />
        <h3 className="mt-4 font-display text-2xl font-extrabold text-[#0B1F3A]">Richiesta ricevuta</h3>
        <p className="mt-2 text-sm text-[#0B1F3A]/70">Abbiamo registrato la tua richiesta di proposta. Il codice è:</p>
        <p data-testid="lead-code" className="mt-2 font-mono text-lg font-bold text-[#F97316]">{done}</p>
        <p className="mt-4 text-sm text-[#0B1F3A]/60">La commerciale verificherà immobili, dotazioni, zona e copertura prima di preparare una proposta dedicata. Ti ricontatteremo nella fascia che hai indicato.</p>
        <button onClick={() => nav("/")} className="mt-6 rounded-full bg-[#0B1F3A] px-6 py-3 text-sm font-bold text-white">Torna alla home</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="lead-form" className="space-y-4 rounded-3xl border border-[#0B1F3A]/10 bg-white p-6 shadow-xl sm:p-8">
      {/* honeypot */}
      <input type="text" name="company_website" value={f.company_website} onChange={set("company_website")} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Nome e cognome" required><input data-testid="lead-name" required value={f.name} onChange={set("name")} className={inputCls} /></Field>
        <Field label="Ragione sociale (facoltativa)"><input data-testid="lead-company" value={f.company} onChange={set("company")} className={inputCls} /></Field>
        <Field label="Ruolo"><select data-testid="lead-role" value={f.role} onChange={set("role")} className={inputCls}><option value="">Seleziona…</option>{ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
        <Field label="Piano di interesse"><select data-testid="lead-plan" value={f.plan_interest} onChange={set("plan_interest")} className={inputCls}><option value="">Seleziona…</option>{PLAN_OPTIONS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}</select></Field>
        <Field label="Email" required><input data-testid="lead-email" type="email" required value={f.email} onChange={set("email")} className={inputCls} /></Field>
        <Field label="Telefono" required><input data-testid="lead-phone" required value={f.phone} onChange={set("phone")} className={inputCls} /></Field>
        <Field label="Numero di unità gestite"><input data-testid="lead-units" value={f.units} onChange={set("units")} className={inputCls} placeholder="es. 5" /></Field>
        <Field label="Tipologia di immobili o struttura"><input data-testid="lead-ptype" value={f.property_type} onChange={set("property_type")} className={inputCls} placeholder="es. bilocali, case vacanza" /></Field>
        <Field label="Zone o CAP"><input data-testid="lead-zones" value={f.zones} onChange={set("zones")} className={inputCls} placeholder="es. Roma centro, 00186" /></Field>
        <Field label="Fascia di metratura"><input data-testid="lead-size" value={f.size_range} onChange={set("size_range")} className={inputCls} placeholder="es. 40-70 mq" /></Field>
        <Field label="N. indicativo di split per unità"><input data-testid="lead-splits" value={f.splits_per_unit} onChange={set("splits_per_unit")} className={inputCls} placeholder="es. 2" /></Field>
        <Field label="Fascia preferita per il ricontatto"><input data-testid="lead-time" value={f.preferred_time} onChange={set("preferred_time")} className={inputCls} placeholder="es. mattina feriali" /></Field>
      </div>

      {isHotel && (
        <div className="grid gap-4 rounded-2xl border border-[#0E7C7B]/20 bg-[#0E7C7B]/5 p-4 sm:grid-cols-2">
          <Field label="Numero di camere"><input data-testid="lead-rooms" value={f.rooms} onChange={set("rooms")} className={inputCls} /></Field>
          <Field label="Spazi comuni"><input data-testid="lead-common" value={f.common_areas} onChange={set("common_areas")} className={inputCls} placeholder="es. reception, sala colazioni" /></Field>
          <p className="sm:col-span-2 text-xs text-[#0B1F3A]/60">Per le strutture ricettive non equipariamo automaticamente ogni camera a un appartamento: prepariamo una proposta dedicata.</p>
        </div>
      )}

      <Field label="Come viene gestita oggi la manutenzione"><textarea data-testid="lead-current" rows={2} value={f.current_management} onChange={set("current_management")} className={inputCls} /></Field>
      <Field label="Principali problemi"><textarea data-testid="lead-problems" rows={2} value={f.main_problems} onChange={set("main_problems")} className={inputCls} /></Field>
      <Field label="Messaggio libero"><textarea data-testid="lead-message" rows={2} value={f.message} onChange={set("message")} className={inputCls} /></Field>

      <label className="flex items-start gap-2 text-sm text-[#0B1F3A]/80">
        <input data-testid="lead-privacy" type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-1 h-4 w-4" />
        <span>Ho letto e accetto l'<a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold text-[#0E7C7B] underline">informativa privacy</a>. <span className="text-[#F97316]">*</span></span>
      </label>
      <label className="flex items-start gap-2 text-sm text-[#0B1F3A]/70">
        <input data-testid="lead-marketing" type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-1 h-4 w-4" />
        <span>Acconsento a ricevere comunicazioni commerciali (facoltativo).</span>
      </label>

      {error && <p data-testid="lead-error" className="text-sm font-semibold text-rose-600">{error}</p>}
      <button data-testid="lead-submit" disabled={busy} type="submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#F97316] py-4 font-bold text-white shadow-lg shadow-[#F97316]/25 transition hover:brightness-110 active:scale-[0.98] disabled:opacity-60">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Invia la richiesta di proposta <ArrowRight className="h-5 w-5" /></>}
      </button>
      <p className="text-center text-xs text-[#0B1F3A]/50">Nessun pagamento è richiesto dal sito. La proposta viene definita dalla commerciale dopo la verifica degli immobili.</p>
    </form>
  );
}
